import type { LearnFAQ } from "@/pages/learn/learnShared";
import {
  makeEsArticle,
  esSaintRelated,
  type EsArticleInput,
} from "./learnFactoryEs";

const reviewBlock = {
  writtenBy: "Neuro Wellness TMS Centers of America",
  reviewedBy: "Daniel Pietras, MD",
  published: "November 3, 2026",
  lastReviewed: "November 3, 2026",
  lastUpdated: "November 3, 2026",
};

const faqs: LearnFAQ[] = [
  {
    question:
      "¿Cuáles son los signos de la depresión resistente al tratamiento?",
    answer:
      "El signo principal es la respuesta inadecuada a al menos dos ensayos de antidepresivos con dosis y duración adecuadas. Otros signos incluyen síntomas persistentes a pesar de la medicación, respuesta parcial que se estanca, episodios recurrentes a pesar del tratamiento y efectos secundarios que impiden alcanzar una dosis adecuada. Una evaluación psiquiátrica puede confirmar si los ensayos previos fueron adecuados.",
  },
  {
    question: "¿Cómo sé si mi depresión es resistente al tratamiento?",
    answer:
      "Si ha probado dos o más antidepresivos a dosis adecuadas durante 6-8 semanas cada uno y su depresión no ha mejorado de forma adecuada, puede cumplir la definición. Una evaluación psiquiátrica puede revisar su historial de medicación, confirmar si los ensayos fueron adecuados y recomendar los siguientes pasos.",
  },
  {
    question:
      "¿La depresión resistente al tratamiento es un diagnóstico aparte?",
    answer:
      "No. La depresión resistente al tratamiento es una descripción de su historial de tratamiento, no un diagnóstico aparte. Significa que los antidepresivos estándar no han funcionado de forma adecuada y que otras opciones pueden ser apropiadas. No significa que su depresión no pueda tratarse.",
  },
  {
    question: "¿Qué cuenta como un ensayo adecuado de antidepresivos?",
    answer:
      "Un ensayo adecuado generalmente significa tomar un antidepresivo a una dosis terapéutica (no solo una dosis inicial) de forma constante durante unas 6-8 semanas. Una medicación suspendida precozmente por efectos secundarios suele considerarse un ensayo incompleto, no un ensayo fallido.",
  },
  {
    question:
      "¿Qué debo hacer si creo que tengo depresión resistente al tratamiento?",
    answer:
      "Solicite una evaluación psiquiátrica completa. Esta confirma si los ensayos previos fueron adecuados, detecta condiciones que pueden imitar la resistencia al tratamiento e identifica qué probar a continuación. Las opciones incluyen TMS, SAINT®, SPRAVATO®, optimización de medicación y psicoterapia.",
  },
];

export const esSignsOfTrd = makeEsArticle({
  slug: "signs-of-treatment-resistant-depression",
  title: "Signos de la depresión resistente al tratamiento | Neuro Wellness",
  description:
    "Signos de que la depresión puede ser resistente al tratamiento: respuesta inadecuada a los antidepresivos, síntomas persistentes y cuándo considerar TMS, SAINT® o SPRAVATO®.",
  h1: "¿Cuáles son los signos de la depresión resistente al tratamiento?",
  breadcrumbLabel: "Signos de la TRD",
  shortAnswer:
    "El signo principal de la depresión resistente al tratamiento es la respuesta inadecuada a al menos dos ensayos de antidepresivos con dosis y duración adecuadas en el episodio actual. Otros signos incluyen síntomas persistentes a pesar de la medicación, respuesta parcial que se estanca, episodios recurrentes a pesar del tratamiento y efectos secundarios que impiden una dosificación adecuada. La depresión resistente al tratamiento no es un diagnóstico aparte — describe su historial de tratamiento. Si sospecha de depresión resistente al tratamiento, una evaluación psiquiátrica puede confirmar si los ensayos previos fueron adecuados y qué probar a continuación.",
  keyTakeaways: [
    "TRD significa respuesta inadecuada a ≥2 ensayos adecuados de antidepresivos.",
    "Los síntomas persistentes a pesar de la medicación son el signo clave.",
    "Una respuesta parcial que se estanca puede indicar TRD.",
    "La TRD es una descripción del historial de tratamiento, no un diagnóstico aparte.",
    "Una evaluación psiquiátrica puede confirmar si los ensayos previos fueron adecuados.",
  ],
  sections: [
    {
      heading: "El signo principal: respuesta inadecuada a la medicación",
      paragraphs: [
        "El signo principal de la depresión resistente al tratamiento es que su depresión no ha mejorado de forma adecuada tras al menos dos ensayos de antidepresivos con dosis y duración adecuadas. 'Adecuado' significa que la medicación se tomó a una dosis terapéutica (no solo una dosis inicial) de forma constante durante unas 6-8 semanas. Una medicación suspendida precozmente por efectos secundarios suele considerarse un ensayo incompleto, no un ensayo fallido.",
        "Si ha probado dos o más antidepresivos a dosis adecuadas durante el tiempo adecuado y su depresión no ha mejorado, puede cumplir la definición de depresión resistente al tratamiento.",
      ],
    },
    {
      heading: "Otros signos",
      paragraphs: [
        "Otros signos que pueden indicar depresión resistente al tratamiento incluyen:",
      ],
      list: [
        "Síntomas persistentes a pesar de tomar la medicación según lo prescrito",
        "Respuesta parcial que se estanca — cierta mejoría pero no remisión completa",
        "Episodios recurrentes a pesar del tratamiento continuo",
        "Efectos secundarios que impiden alcanzar una dosis adecuada",
        "Depresión que empeora o reaparece tras una mejoría inicial",
        "Múltiples cambios de medicación sin una mejoría sostenida",
      ],
    },
    {
      heading: "Lo que no es la depresión resistente al tratamiento",
      paragraphs: [
        "La depresión resistente al tratamiento no es un diagnóstico aparte — es una descripción de su historial de tratamiento. No significa que su depresión no pueda tratarse. No significa que haya 'fracasado' en el tratamiento. Significa que los tratamientos estándar de primera línea (antidepresivos) no han funcionado de forma adecuada y que otras opciones pueden ser apropiadas.",
        "Tampoco significa que tenga que haber probado todos los tratamientos disponibles. La definición suele requerir al menos dos ensayos fallidos de antidepresivos, no el agotamiento de todas las opciones.",
      ],
    },
    {
      heading: "Qué hacer si sospecha de TRD",
      paragraphs: [
        "Si sospecha de depresión resistente al tratamiento, solicite una evaluación psiquiátrica completa. Esta revisión confirma si los ensayos previos de medicación fueron realmente adecuados, detecta condiciones que pueden imitar la resistencia al tratamiento (como el trastorno bipolar) e identifica qué probar a continuación. Las opciones más allá de los antidepresivos estándar incluyen TMS, SAINT® TMS acelerada, SPRAVATO® (esketamina), optimización de medicación y psicoterapia.",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Fava M. Diagnosis and definition of treatment-resistant depression. Biol Psychiatry. 2003;53(8):649-659.",
      url: "https://pubmed.ncbi.nlm.nih.gov/12706951/",
    },
  ],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);
